import { useEffect, useRef, useCallback, type CSSProperties } from 'react';
import type { PublicStoreConfig } from '../types';
import type { WidgetConfig } from '@cartiq/shared';
import ChatWindow from './ChatWindow';
import { WIDGET_PANEL_WIDTHS } from '../widgetConfig';

interface Props {
  slug: string;
  config: PublicStoreConfig;
  open: boolean;
  onClose: () => void;
  widgetConfig: WidgetConfig;
}

export function ChatDrawer({ slug, config, open, onClose, widgetConfig }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number | null>(null);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        onClose();
      }
    },
    [onClose],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (startY.current === null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 80) {
        onClose();
        startY.current = null;
      }
    },
    [onClose],
  );

  const handleTouchEnd = useCallback(() => {
    startY.current = null;
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    const panel = sheetRef.current;
    panel?.focus({ preventScroll: true });
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !panel) return;
      const controls = [...panel.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), [tabindex="0"]')]
        .filter(element => element.getClientRects().length > 0);
      const first = controls[0], last = controls.at(-1);
      if (!first || !last) { event.preventDefault(); panel.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    panel?.addEventListener('keydown', keydown);
    return () => {
      panel?.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className={`cartiq-drawer fixed inset-0 z-[60] flex transition-all duration-300 ease-out ${
        open
          ? 'pointer-events-auto bg-primary/40 backdrop-blur-sm'
          : 'pointer-events-none bg-transparent'
      }`}
      aria-hidden={!open}
    >
      <div
        ref={sheetRef}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
        aria-label={`${config.name || 'Store'} shopping assistant`}
        tabIndex={open ? -1 : undefined}
        className={`mt-auto flex h-[92dvh] w-full flex-col overflow-hidden rounded-t-[24px] bg-surface shadow-2xl transition-transform duration-300 ease-out sm:my-6 sm:h-[calc(100dvh-3rem)] sm:max-w-[var(--chat-panel-width)] sm:rounded-[24px] ${
          widgetConfig.position === 'bottom-left' ? 'sm:ml-6 sm:mr-auto' : 'sm:ml-auto sm:mr-6'
        } ${
          open
            ? 'translate-y-0 sm:translate-x-0'
            : `translate-y-full ${
                widgetConfig.position === 'bottom-left'
                  ? 'sm:-translate-x-[120%] sm:translate-y-0'
                  : 'sm:translate-x-[120%] sm:translate-y-0'
              }`
        }`}
        style={{ '--chat-panel-width': `${WIDGET_PANEL_WIDTHS[widgetConfig.panelSize]}px` } as CSSProperties}
      >
        {open && (
          <>
            <div className="flex shrink-0 items-center justify-center py-2 sm:hidden"
              onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
              <div className="h-1 w-10 rounded-full bg-border" />
            </div>

            <div className="flex-1 overflow-hidden">
              <ChatWindow
                slug={slug}
                config={config}
                onClose={onClose}
                embedded
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChatDrawer;
